import * as React from "react";
import { useCallback, useMemo, useState } from "react";
import styled from "styled-components";
import { Formik, Form } from "formik";
import useProductName from "brand-customization/useProductName";
import FormikInput from "components/common/FormikInput";
import ModalSubmit from "components/common/ModalSubmit";
import DisclaimerModalContent from "security-app/components/Investigations/AIReport/DisclaimerModalContent";
import Modal from "components/bootstrap/Modal";
import { IconButton } from "components/common";
import useAssistantSidebar from "ai/sidebar/useAssistantSidebar";
import useWidgetResults from "views/components/useWidgetResults";
import useView from "views/hooks/useView";
import createWidgetSummaryRequest from "ai/widget-summary/createWidgetSummaryRequest";
import useAITermsAcceptance from "ai/hooks/useAITermsAcceptance";
import { TELEMETRY_EVENT_TYPE } from "telemetry/Constants";
import usePersistedSetting from "hooks/usePersistedSetting";
import { updateWidget } from "views/logic/slices/widgetActions";
import useViewsDispatch from "views/stores/useViewsDispatch";
import SaveContextInfo from "ai/common/SaveContextInfo";
import useLicenseCheck from "license/useLicenseCheck";
import useAIConfiguration from "ai/hooks/useAIConfiguration";
const StyledIconButton = styled(IconButton)`
  span {
    position: relative;
    left: -1px;
  }
`;
const useDoNotAskAgain = () => usePersistedSetting("views.widgetSummary.doNotAskAgain");
const telemetryEvents = {
  cancel: TELEMETRY_EVENT_TYPE.WIDGET_SUMMARY.TERMS_AND_CONDITIONS_CANCELED,
  consent: TELEMETRY_EVENT_TYPE.WIDGET_SUMMARY.TERMS_AND_CONDITIONS_CONSENT
};
const checkPrerequisites = (license, loadingAcceptance, widget, widgetResult, productName, aiFeaturesEnabled, termsAcceptanceError) => {
  var _a, _b, _c, _d;
  const invalidLicense = !((_a = license == null ? void 0 : license.security) == null ? void 0 : _a.isValid) && !((_b = license == null ? void 0 : license.enterprise) == null ? void 0 : _b.isValid);
  const widgetEmpty = (_c = widget == null ? void 0 : widget.config) == null ? void 0 : _c.isEmpty;
  const noData = ((_d = widgetResult == null ? void 0 : widgetResult.chart) == null ? void 0 : _d.total) === 0;
  const actionDisabled = invalidLicense || loadingAcceptance || widgetEmpty || noData || !aiFeaturesEnabled || termsAcceptanceError;
  let disabledReason = "";
  if (termsAcceptanceError) {
    disabledReason = `Error fetching terms: ${termsAcceptanceError}`;
  } else if (!aiFeaturesEnabled) {
    disabledReason = "AI features are disabled by the administrator.";
  } else if (invalidLicense) {
    disabledReason = `No valid ${productName} Enterprise or Security license.`;
  } else if (loadingAcceptance) {
    disabledReason = "Loading terms status ...";
  } else if (widgetEmpty) {
    disabledReason = "Widget is empty.";
  } else if (noData) {
    disabledReason = "No data to analyze.";
  }
  return { actionDisabled, disabledReason };
};
const WidgetSummaryActionComponent = ({ widget }) => {
  const license = useLicenseCheck();
  const productName = useProductName();
  const { generate } = useAssistantSidebar();
  const { widgetData: widgetResult } = useWidgetResults(widget.id);
  const view = useView();
  const generateForWidget = useCallback(
    (_widget) => generate(createWidgetSummaryRequest(view, _widget, widgetResult)),
    [generate, view, widgetResult]
  );
  const [requiresContext, setRequiresContext] = useState(false);
  const [requiresConsent, setRequiresConsent] = useState(false);
  const {
    data: { accepted, error: termsAcceptanceError },
    isLoading: loadingAcceptance,
    refetch
  } = useAITermsAcceptance();
  const [doNotAskAgain, setDoNotAskAgain] = useDoNotAskAgain();
  const dispatch = useViewsDispatch();
  const { data: aiConfig, isLoading: loadingAIConfig } = useAIConfiguration();
  const aiFeaturesEnabled = useMemo(
    () => !loadingAIConfig && (aiConfig == null ? void 0 : aiConfig.isEnabled) === true && !termsAcceptanceError,
    [aiConfig == null ? void 0 : aiConfig.isEnabled, loadingAIConfig, termsAcceptanceError]
  );
  const onClick = useCallback(() => {
    if (!accepted) {
      setRequiresConsent(true);
    }
    const widgetRequiresContext = !widget.description || !widget.context;
    if (widgetRequiresContext && !doNotAskAgain) {
      setRequiresContext(true);
    }
    if (accepted && (!widgetRequiresContext || doNotAskAgain)) {
      generateForWidget(widget);
    }
  }, [accepted, doNotAskAgain, generateForWidget, widget]);
  const onConsent = useCallback(() => {
    setRequiresConsent(false);
    refetch();
  }, [refetch]);
  const clearRequestWaitingForConsent = useCallback(() => {
    setRequiresConsent(false);
    setRequiresContext(false);
  }, []);
  const modal = useMemo(() => {
    var _a, _b;
    if (requiresConsent) {
      return /* @__PURE__ */ React.createElement(Modal, { show: true, onHide: clearRequestWaitingForConsent, bsSize: "large" }, /* @__PURE__ */ React.createElement(
        DisclaimerModalContent,
        {
          onClose: clearRequestWaitingForConsent,
          onConsent,
          telemetryEvents
        }
      ));
    }
    if (requiresContext) {
      const close = () => setRequiresContext(false);
      const initialValues = {
        context: (_a = widget == null ? void 0 : widget.context) != null ? _a : "",
        description: (_b = widget == null ? void 0 : widget.description) != null ? _b : "",
        doNotAsk: doNotAskAgain != null ? doNotAskAgain : false
      };
      const onSave = async (values) => {
        if (values.doNotAsk !== doNotAskAgain) {
          setDoNotAskAgain(values.doNotAsk);
        }
        if (values.context !== initialValues.context || values.description !== initialValues.description) {
          const newWidget = widget.toBuilder().description(values.description).context(values.context).build();
          await dispatch(updateWidget(widget.id, newWidget));
          generateForWidget(newWidget);
        } else {
          generateForWidget(widget);
        }
        close();
      };
      return /* @__PURE__ */ React.createElement(Modal, { show: true, onHide: close }, /* @__PURE__ */ React.createElement(Modal.Header, null, /* @__PURE__ */ React.createElement("h3", null, "Adding more context to Widget")), /* @__PURE__ */ React.createElement(Modal.Body, null, /* @__PURE__ */ React.createElement(Formik, { initialValues, onSubmit: onSave }, /* @__PURE__ */ React.createElement(Form, null, /* @__PURE__ */ React.createElement("p", null, "Adding a clear description and relevant context helps clarify your widget's purpose and guides users on interpreting its results. This ensures the widget summary feature delivers the most accurate and insightful analysis possible. Would you like to add one now?"), /* @__PURE__ */ React.createElement(FormikInput, { label: "Description", name: "description", id: "description" }), /* @__PURE__ */ React.createElement(FormikInput, { label: "Context", name: "context", type: "textarea", id: "context", rows: 12 }), /* @__PURE__ */ React.createElement(FormikInput, { label: "Do not ask me again", name: "doNotAsk", type: "checkbox", id: "doNotAsk" }), /* @__PURE__ */ React.createElement(SaveContextInfo, null), /* @__PURE__ */ React.createElement(ModalSubmit, { submitButtonText: "Run", displayCancel: false })))));
    }
    return null;
  }, [
    clearRequestWaitingForConsent,
    dispatch,
    doNotAskAgain,
    generateForWidget,
    onConsent,
    requiresConsent,
    requiresContext,
    setDoNotAskAgain,
    widget
  ]);
  const { actionDisabled, disabledReason } = useMemo(
    () => checkPrerequisites(
      license,
      loadingAcceptance,
      widget,
      widgetResult,
      productName,
      aiFeaturesEnabled,
      termsAcceptanceError
    ),
    [license, loadingAcceptance, widget, widgetResult, productName, aiFeaturesEnabled, termsAcceptanceError]
  );
  return /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(
    StyledIconButton,
    {
      disabled: actionDisabled,
      name: "note_add",
      iconType: "regular",
      title: `Generate Widget Summary${actionDisabled ? ` - Disabled: ${disabledReason}` : ""}`,
      onClick
    }
  ), modal);
};
export default WidgetSummaryActionComponent;
